import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { IClientPortalCasesService } from "services/ClientPortalCasesService";
import { ClientPortalCaseDetailsModel, ClientPortalNewCaseModel } from "services/api/cplapi/CPLAPI";
import { SERVICE_TYPES } from "setup/dependency/ServiceTypes";
import { container } from "setup/dependency/inversify.config";
import { RootState } from "setup/redux/RootReducer";

type CaseState = {
  Case: ClientPortalCaseDetailsModel | null;
  LoadingCase: boolean;
  EditingCase: boolean;
  CreatedCaseID: number;
};

const initialState: CaseState = {
  Case: null,
  LoadingCase: false,
  EditingCase: false,
  CreatedCaseID: 0,
};

export const CaseDetailsSlice = createSlice({
  name: "CaseDetails",
  initialState,
  reducers: {
    resetCreatedCaseID: (state) => {
      state.CreatedCaseID = 0;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getCaseDetails.pending, (state) => {
        state.LoadingCase = true;
      })
      .addCase(getCaseDetails.fulfilled, (state, action) => {
        state.Case = action.payload;
        state.LoadingCase = false;
      })
      .addCase(getCaseDetails.rejected, (state) => {
        state.Case = new ClientPortalCaseDetailsModel();
        state.LoadingCase = false;
      })
      .addCase(createCase.pending, (state) => {
        state.EditingCase = true;
      })
      .addCase(createCase.fulfilled, (state, action) => {
        state.EditingCase = false;
        state.CreatedCaseID = action.payload;
      })
      .addCase(createCase.rejected, (state) => {
        state.EditingCase = false;
        state.CreatedCaseID = 0;
      });
  },
});

export const getCaseDetails = createAsyncThunk<ClientPortalCaseDetailsModel, { caseID: number }>(
  "CaseDetails/GetCaseDetails",
  async ({ caseID }) => {
    const casesService = container.get<IClientPortalCasesService>(SERVICE_TYPES.ClientPortalCasesService);
    return await casesService.getCaseDetails(caseID);
  }
);

export const createCase = createAsyncThunk(
  "CaseDetails/CreateCase",
  async (newCaseModel: ClientPortalNewCaseModel, thunkAPI) => {
    const casesService = container.get<IClientPortalCasesService>(SERVICE_TYPES.ClientPortalCasesService);
    const createdCaseID = await casesService.createCase(newCaseModel);
    thunkAPI.dispatch(getCaseDetails({ caseID: createdCaseID }));
    return createdCaseID;
  }
);

export const { resetCreatedCaseID } = CaseDetailsSlice.actions;

export const selectCase = (state: RootState) => state.caseDetails.Case;
export const selectLoadingCase = (state: RootState) => state.caseDetails.LoadingCase;
export const selectEditingCase = (state: RootState) => state.caseDetails.EditingCase;
export const selectCreatedCaseID = (state: RootState) => state.caseDetails.CreatedCaseID;

export default CaseDetailsSlice.reducer;
